/**
 * DOC-1 — Entry variant schemas + runtime validation.
 *
 * Every entry written through DocService.write() is validated against
 * the variant schema for its entryVariant field. Invalid entries are
 * routed to doc_rejections — never to doc_entries.
 *
 * Variants:
 *   agent_decision        — AEGIS agent action / swarm consensus
 *   event_emission        — event bus publication
 *   human_override        — operator manual action overriding an agent
 *   external_integration  — outbound API / EDI / webhook call
 *   system_event          — platform lifecycle event (deploy, startup, etc.)
 */

// Fields every entry must have, regardless of variant.
const UNIVERSAL_REQUIRED = [
  'entryId',
  'tenantId',
  'actorId',
  'actorType',
  'payload',
  'payloadHash',
  'createdAt',
];

const ACTOR_TYPES = ['agent', 'user', 'system', 'external'];

const VARIANT_REQUIRED = {
  agent_decision: ['confidence', 'reasoningChain'],
  event_emission: ['eventTopic'],
  human_override: ['actorId'],
  external_integration: ['payload'],
  system_event: [],
  // AGENT-2 variants — answered by DOC-1 substrate design (see spec Deliverable 2)
  agent_handoff: ['actorId'],
  sentinel_violation: ['actorId'],
  workflow_state_transition: ['eventTopic'],
  // escalation_requested consumed by Mission Control DEFAULT_VARIANTS (AGENT-3)
  escalation_requested: ['actorId'],
  // AEGIS-42: meta-orchestration decision audit entry — HMAC-signed per DOC-1 substrate.
  // Schema: payload must carry { triggerType, decision, rationale, agentsInvolved, entityType, entityId }.
  // correlationId is stored internally only — must not be surfaced in API responses.
  meta_orchestration: ['actorId'],
  // ┌─── AEGIS-7-EXT ─────────────────────
  // VEC-001 patent substrate audit variants (Claims A/B/C/F).
  // Payload requirements per variant:
  //   vec_dissent_record:       { recordId, decisionContextId, agentA, agentB, deltaScore }
  //   vec_premortem_record:     { recordId, decisionContextId, impactEstimate, failurePathCount }
  //   vec_permission_request:   { requestId, agentClass, constraintViolated, proposedAction }
  //   vec_temperament_change:   { agentClass, previousVersion, newVersion, changedBy }
  vec_dissent_record:     ['actorId'],
  vec_premortem_record:   ['actorId'],
  vec_permission_request: ['actorId'],
  vec_temperament_change: ['actorId'],
  // └─── END AEGIS-7-EXT ─────────────────
  // ┌─── AEGIS-UI-PRIMITIVES-1 ──────────────────────────────
  // Editorial opener synthesis audit — one entry per EditorialOpener render.
  // Payload carries surface, module, sentence, confidence, entityContextKeys.
  aegis_editorial_opener_synthesized: [],
  // └─── END AEGIS-UI-PRIMITIVES-1 ────────────────────────
};

const VARIANT_KEYS = Object.keys(VARIANT_REQUIRED);

/**
 * Validate a prepared entry (post-ULID, post-hash assignment).
 * Returns an array of error strings — empty array means valid.
 *
 * @param {Object} entry
 * @returns {string[]}
 */
export function validateDocEntry(entry) {
  const errors = [];

  // Universal required fields
  for (const field of UNIVERSAL_REQUIRED) {
    if (entry[field] === undefined || entry[field] === null || entry[field] === '') {
      errors.push(`missing required field: ${field}`);
    }
  }

  // actorType enum
  if (entry.actorType && !ACTOR_TYPES.includes(entry.actorType)) {
    errors.push(`invalid actorType "${entry.actorType}" — must be one of: ${ACTOR_TYPES.join(', ')}`);
  }

  // entryVariant presence and enum
  if (!entry.entryVariant) {
    errors.push(`missing required field: entryVariant`);
  } else if (!VARIANT_KEYS.includes(entry.entryVariant)) {
    errors.push(`invalid entryVariant "${entry.entryVariant}" — must be one of: ${VARIANT_KEYS.join(', ')}`);
  } else {
    // Variant-specific required fields
    for (const field of VARIANT_REQUIRED[entry.entryVariant]) {
      if (entry[field] === undefined || entry[field] === null || entry[field] === '') {
        errors.push(`${entry.entryVariant} requires field: ${field}`);
      }
    }
  }

  // confidence bounds for agent decisions
  if (entry.entryVariant === 'agent_decision' && entry.confidence !== undefined) {
    const c = Number(entry.confidence);
    if (!Number.isFinite(c) || c < 0 || c > 1) {
      errors.push('confidence must be a number in [0, 1]');
    }
  }

  // parentEntryIds must be an array when present
  if (entry.parentEntryIds !== undefined && !Array.isArray(entry.parentEntryIds)) {
    errors.push('parentEntryIds must be an array');
  }

  return errors;
}

/**
 * Convenience builders for each entry variant.
 * These construct a valid entry payload for DocService.write().
 */
export const EntryBuilders = {
  agentDecision({ tenantId, actorId, agentId, module, decision, confidence, reasoningChain, relatedEntityId, relatedEntityType, modelVersion, parentEntryIds = [], eventTopic = null }) {
    return {
      entryVariant: 'agent_decision',
      tenantId,
      actorId: actorId || agentId || 'aegis',
      actorType: 'agent',
      eventTopic,
      parentEntryIds,
      payload: { agentId, module, decision, relatedEntityId, relatedEntityType },
      inputHashes: [],
      modelVersion: modelVersion || null,
      reasoningChain: reasoningChain || '',
      confidence: Number(confidence ?? 0.7),
      attestationRef: null,
    };
  },

  eventEmission({ tenantId, actorId, actorType = 'system', eventTopic, payload, parentEntryIds = [] }) {
    return {
      entryVariant: 'event_emission',
      tenantId,
      actorId,
      actorType,
      eventTopic,
      parentEntryIds,
      payload,
      inputHashes: [],
      modelVersion: null,
      reasoningChain: null,
      confidence: null,
      attestationRef: null,
    };
  },

  humanOverride({ tenantId, actorId, action, originalEntryId, reason, payload, parentEntryIds = [] }) {
    return {
      entryVariant: 'human_override',
      tenantId,
      actorId,
      actorType: 'user',
      eventTopic: null,
      parentEntryIds: originalEntryId ? [...parentEntryIds, originalEntryId] : parentEntryIds,
      payload: { action, reason, ...payload },
      inputHashes: [],
      modelVersion: null,
      reasoningChain: reason || null,
      confidence: null,
      attestationRef: null,
    };
  },

  externalIntegration({ tenantId, actorId = 'system', integrationType, endpoint, payload, parentEntryIds = [] }) {
    return {
      entryVariant: 'external_integration',
      tenantId,
      actorId,
      actorType: 'external',
      eventTopic: null,
      parentEntryIds,
      payload: { integrationType, endpoint, ...payload },
      inputHashes: [],
      modelVersion: null,
      reasoningChain: null,
      confidence: null,
      attestationRef: null,
    };
  },

  systemEvent({ tenantId, actorId = 'system', eventName, payload = {}, parentEntryIds = [] }) {
    return {
      entryVariant: 'system_event',
      tenantId,
      actorId,
      actorType: 'system',
      eventTopic: eventName || null,
      parentEntryIds,
      payload: { eventName, ...payload },
      inputHashes: [],
      modelVersion: null,
      reasoningChain: null,
      confidence: null,
      attestationRef: null,
    };
  },

  // ── AGENT-2 variants (DOC-1 substrate design resolves the four architecture questions)

  agentHandoff({ tenantId, actorId, sourceAgentId, targetAgentId, capability, oauthScope, workflowId, payload = {}, parentEntryIds = [] }) {
    return {
      entryVariant: 'agent_handoff',
      tenantId,
      actorId,
      actorType: 'agent',
      eventTopic: `agent.handoff.${capability || 'unknown'}`,
      parentEntryIds,
      payload: { sourceAgentId, targetAgentId, capability, oauthScope, workflowId, ...payload },
      inputHashes: [],
      modelVersion: null,
      reasoningChain: null,
      confidence: null,
      attestationRef: null,
    };
  },

  sentinelViolation({ tenantId, actorId = 'sentinel', offendingEntryId, rule, description, payload = {}, parentEntryIds = [] }) {
    return {
      entryVariant: 'sentinel_violation',
      tenantId,
      actorId,
      actorType: 'agent',
      eventTopic: `sentinel.violation.${rule || 'unknown'}`,
      parentEntryIds: offendingEntryId ? [...parentEntryIds, offendingEntryId] : parentEntryIds,
      payload: { rule, description, offendingEntryId, ...payload },
      inputHashes: [],
      modelVersion: null,
      reasoningChain: description || null,
      confidence: null,
      attestationRef: null,
    };
  },

  workflowStateTransition({ tenantId, actorId, workflowId, fromState, toState, eventTopic, payload = {}, parentEntryIds = [] }) {
    return {
      entryVariant: 'workflow_state_transition',
      tenantId,
      actorId,
      actorType: 'agent',
      eventTopic: eventTopic || `workflow.state.${toState || 'unknown'}`,
      parentEntryIds,
      payload: { workflowId, fromState, toState, ...payload },
      inputHashes: [],
      modelVersion: null,
      reasoningChain: null,
      confidence: null,
      attestationRef: null,
    };
  },

  /**
   * AEGIS-42: meta-orchestration decision entry.
   * Written by MetaOrchestrationService for every routing, conflict resolution,
   * budget check, and plan-and-execute decision. HMAC-signed per DOC-1 substrate.
   *
   * IMPORTANT: correlationId is stored in payload.correlationId for internal
   * traceability only — never expose this field in external API responses.
   *
   * @param {object} params
   * @param {string}   params.tenantId
   * @param {string}   params.actorId         — 'meta_orchestrator' or agent id
   * @param {string}   params.triggerType      — 'conflict' | 'routing' | 'budget_check' | 'plan_step'
   * @param {object}   params.decision         — structured decision object (resolution, model, etc.)
   * @param {string}   params.rationale        — human-readable explanation
   * @param {string[]} params.agentsInvolved   — agent IDs participating in the decision
   * @param {string}   params.entityType       — 'load' | 'shipment' | 'customer' | 'sku' | 'invoice'
   * @param {string}   params.entityId         — ID of the affected entity
   * @param {string}   params.correlationId    — INTERNAL ONLY — trace ID, never expose to callers
   * @param {string}   [params.modelUsed]      — model tier used for the decision
   * @param {object}   [params.tokensConsumed] — { input, output }
   * @param {number}   [params.latencyMs]
   * @param {string[]} [params.parentEntryIds]
   */
  metaOrchestration({
    tenantId, actorId = 'meta_orchestrator', triggerType, decision, rationale,
    agentsInvolved = [], entityType, entityId, correlationId,
    modelUsed = null, tokensConsumed = { input: 0, output: 0 }, latencyMs = 0,
    parentEntryIds = [],
  }) {
    return {
      entryVariant: 'meta_orchestration',
      tenantId,
      actorId,
      actorType: 'agent',
      eventTopic: `aegis.meta_orchestration.${triggerType || 'unknown'}`,
      parentEntryIds,
      payload: {
        triggerType,
        decision,
        rationale,
        agentsInvolved,
        entityType,
        entityId,
        correlationId,  // INTERNAL ONLY — never surface to external API consumers
        modelUsed,
        tokensConsumed,
        latencyMs,
      },
      inputHashes: [],
      modelVersion: modelUsed || null,
      reasoningChain: rationale || null,
      confidence: null,
      attestationRef: null,
    };
  },
};

export const ENTRY_VARIANTS = VARIANT_KEYS;
export const VALID_ACTOR_TYPES = ACTOR_TYPES;
